import {useState, useEffect, useRef} from 'react'
import {useForm} from 'react-hook-form';
import {Button, Row, Col, Form} from 'react-bootstrap';
import {compose} from '../../lib/compose';
import {usePortalContext} from '../../lib/context/portalContext';
import withAlerts from '../../lib/withAlerts';
import {useUserMutations} from './userMutations'
import {NIL} from 'uuid';
import {useDeviceMutations} from '../devices/deviceMutations';
import { gql } from "@apollo/client";
import { useLazyQueryCompat as useLazyQuery } from '../../lib/apollo/mutationCompat';
import {ErrorMessage} from '@hookform/error-message';
// import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
// import {faSearch} from '@fortawesome/pro-light-svg-icons';
import cx from 'classnames';
import {isPossiblePhoneNumber} from 'libphonenumber-js';
import {DeleteUserConfirmModal} from './deleteUserConfirmModal';
import MdmDetail from './mdmDetails';
import {useFeatureContext} from '../../lib/context/featureContext';
import {useCustomerContext} from '../../lib/context/customerContext';
import {Can} from '../auth/can';

const UserDetails = (props) => {
  const {togglePortal, rowData, origin} = usePortalContext();
  const {success, error} = props?.alerts || {};
  //first form hook
  const { register, reset, watch, setValue, setError, handleSubmit, setFocus, formState: { errors } } = useForm({mode: 'onBlur'});
  //second form hook
  const {register: assignmentRegister, handleSubmit: assignmentHandleSubmit, setFocus: assignmentSetFocus} = useForm();
  const [ newUsernameError, setNewUsernameError ] = useState(null);
  const [ usernameError, setUsernameError ] = useState(null);
  const [showUserAssign, setShowUserAssign] = useState(false);
  const { setCriteria } = useDeviceMutations();
  const [getUserID] = useLazyQuery(GET_USERID);
  const [getDeviceByUserID] = useLazyQuery(GET_DEVICE_BY_USERID);
  const {add, edit, remove} = useUserMutations();
  const {feature_customer_mdm} = useFeatureContext();
  const {hasPermission} = useCustomerContext();

  // RBAC: USER_EDIT gates editing an EXISTING user's fields + Save Changes
  // button ("On off-canvas: (a) Disable all user information textfields;
  // (b) Disable the 'Save Changes' button."). Creating a NEW user is already
  // gated upstream by USER_CREATE disabling the 'Create User' button in
  // index.jsx, so this only needs to apply when an existing user is loaded.
  // Note: USER_MDM_LINK_EDIT has no listed UI treatment in the RBAC sheet,
  // so MdmDetail below is intentionally left out of this fieldset pending
  // clarification - flag before assuming it should inherit this gate.
  const isEditingExisting = !!rowData?.user?.username;
  const userFieldsDisabled = isEditingExisting && !hasPermission('USER_EDIT');
  
  // reset form with user data after props finish coming in.
  // rowData is updated by portalContext subquery to include 
  // user information
  const loadedUserId = useRef(null);

  useEffect(() => {
    const user = rowData?.user ?? null;
    if (!user || loadedUserId.current === user.id) return;
    loadedUserId.current = user.id;
    reset({ 
      username: user.username, 
      firstname: user.firstname, 
      lastname: user.lastname, 
      email: user.email, 
      phone: user.phone, 
      mdm_device_id: user.mdm_device_id 
    });
  }, [rowData, reset]);

  const handleUnassignUser = async () => {
    const newAssignment = await setCriteria({ variables: 
      {
        serial_number: rowData?.serial_number,
        field: 'user_id',
        value: NIL
      }
    });

    if(newAssignment?.errors) {
      error({msg: 'There was an error unassigning the user. Please try again.'});
    } else {
      success({msg: `User unassigned.`});
    }
    togglePortal();
  }

  const handleAssignUser = async (formData) => {
    setNewUsernameError(null);  
    
    const {newusername} = formData ?? null;
    if(newusername.length === 0) {
      setNewUsernameError('User ID is required.');
      assignmentSetFocus('newusername');
      return;
    }

    if(newusername.length > 0) {
      //build vars
      const variables = {
        variables: {
          filters: {
            field: "username", value: `${newusername}`
          }
        }
      };

      //lazyQuery to verify username exists
      const {data, errors} = await getUserID(variables);
      
      if(data?.Users && data.Users.result.length) {
        const id = data.Users.result[0].id ?? null;

        //userid to assign to safecase
        if (id) {

          const deviceCheckVariables =  {
            variables: {
              filters: {
                combineAnd: [
                  {field: "user_id", value: `${id}` },
                  {field: "with.user", value: "1"},
                ]
              }
            }
          }
          //lazy query to check for assignments
          const {data: dCheck} = await getDeviceByUserID(deviceCheckVariables);

          if(dCheck?.Devices) {
            setNewUsernameError('User already assigned to a SafeCase. Please unassign first.');
            assignmentSetFocus('newusername');
            return;
          }
          
          const newAssignment = await setCriteria({
            variables: {
              serial_number: `${rowData?.serial_number}`,
              field: 'user_id',
              value: id
            }
          });

          if (newAssignment?.errors) {
            error({msg: 'There was an error assigning the user. Please try again.' });
          } else {
            success({msg: `User assigned.`});
          }
        }
        togglePortal();
      }

      if(errors) {
        //no user found by username search
        setNewUsernameError('User ID not found.')
        assignmentSetFocus('newusername');
      }
    }
  }

  //attempt to strip duplicate values from user update to prevent excess logging
  function removeDuplicateValues(newObject, originalObject) {
    for (const property in newObject) {
      if (property === "username") continue;
      if (originalObject[property] === newObject[property]) {
          delete newObject[property];
      }
    }
  }
  
  // handle delete user
  const handleDeleteUser = async() => {
    const deletedUser = await remove({variables: {user_id: rowData?.user?.id}});

    if(deletedUser?.errors) {

      error({msg: 'There was an error deleting the user. Please try again.'});
      
    } else {
      success({msg: `User deleted.`});
      togglePortal();
    }
  }

  // when user hits submit
  const handleSaveUser = async(formData) => {
    
    setUsernameError(null);
    // if row doesn't have a username, we got here from 'create new user'
    if(!(rowData?.user?.username)) {

      const {username} = formData ?? null;
      //inline error
      if(username.length === 0) {
        setUsernameError('User ID is required.');
        setFocus('username');
        return;
      }

      if(username.length > 0) {
        //build vars
        const variables = {
          variables: {
            filters: {
              field: "username", value: `${username}`
            }
          }
        };

        //lazyQuery
        const {data} = await getUserID(variables);
        if(data?.Users) {
          setUsernameError('User ID already exists.');
          setFocus('username');
          return;   
        }

        //attempt to strip empty values from user create
        for (const property in formData) {
          if (formData[property] === "") {
              delete formData[property];
          }
        }
        
        const newUser = await add({variables: {user: formData}});
        
        if(newUser?.errors) {
          //search the message portion first
          const message = newUser?.errors?.[0]?.message;
          if(message.includes('USR-004') || message.includes('user_mdm_device_id_key')) {
            error({msg: 'This MDM device ID has already been enrolled.'});  
          } else {
            error({msg: 'There was an error creating the new user. Please try again.'});
          }          
        } else {
          success({msg: "User created."});
          togglePortal();
        }
      }
    }
    // else we're editing a user
    else {

      //strip out the duplicate values
      removeDuplicateValues(formData, rowData?.user);

      const extendedFormData = {user_id: rowData?.user?.id, ...formData};
      const editUser = await edit({variables: {user: extendedFormData}})
      
      if(editUser?.errors) {
        //search the message portion first
        const message = editUser?.errors?.[0]?.message;
        if(message.includes('USR-004') || message.includes('user_mdm_device_id_key')) {
          error({msg: 'This MDM device ID has already been enrolled.'});  
        } else {
          error({msg: 'User edit failed. Please try again.'});
        }
        
      } else {
        success({msg: `User edited.`});
        togglePortal();
      }
    }
  }

  const hasPhone = async () => {
    setError('phone', { type: 'manual' });
    setFocus('phone');  
  }
  
  return (
    <div className="offcanvas-form">
      { ( (origin?.pathname === '/users') || 
      (origin?.pathname === '/devices' && rowData?.user?.username) || 
      (origin?.pathname === '/policy/assignments' && rowData?.user?.username)
      ) && 
      <Form className="mb-3" onSubmit={handleSubmit(handleSaveUser)}>
        <fieldset disabled={userFieldsDisabled}>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label >User ID</Form.Label>
              {/* default comes from clicked row context
              BUT if it was a device page row?
               defaultValue={rowData?.firstname} */}
              <Form.Control
              
              {...register("username", {
                required: "User ID is required.",
                pattern: {
                  value: /^[a-zA-Z0-9-_.]+$/,
                  message: "Invalid User ID - Letters, numbers, hyphens, underscores and periods are acceptable."
                },
                maxLength: {
                  value: 50,
                  message: "Length of User ID exceeds 50 characters."
                }
              })}
                type="text"
                data-testid="username_input"
                placeholder="JohnDoe01" 
                defaultValue={rowData?.user?.username}
                readOnly={isEditingExisting}
                className={cx({'error-input': errors?.username || usernameError})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label >First</Form.Label>
              <Form.Control {...register("firstname", {
                maxLength: {
                  value: 50,
                  message: "Length of First name exceeds 50 characters."
                }})}
                type="fname"
                data-testid="fname_input"
                placeholder="John"
                className={cx({'error-input': errors?.firstname})}
                defaultValue={rowData?.user?.firstname}/>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label >Last</Form.Label>
              <Form.Control {...register("lastname", {

                maxLength: {
                  value: 50,
                  message: "Length of Last name exceeds 50 characters."
                }})}
                type="lname"
                data-testid="lname_input"
                placeholder="Doe"
                className={cx({'error-input': errors?.lastname})}
                defaultValue={rowData?.user?.lastname}/>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                {...register("email", {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address."
                  },
                  maxLength: {
                    value: 50,
                    message: "Length of Email exceeds 50 characters."
                  }  
                })}
                type="email"
                data-testid="email_input"
                placeholder="name@email.com" 
                defaultValue={rowData?.user?.email} 
                className={cx({'error-input': errors?.email})}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                {...register("phone", {
                  validate: {
                    hasAndValid: v => { 
                      if (!v) {
                        return;
                      } else {
                        return(
                        v.length && (isPossiblePhoneNumber(v) || isPossiblePhoneNumber(v, 'US')) || "Phone format invalid"
                        );
                      }
                      
                    }
                  }
                })}
              type="phone"
              data-testid="phone_input"
              placeholder="555-555-5555" 
              defaultValue={rowData?.user?.phone}
              className={cx({'error-input': errors?.phone})}
            />
            </Form.Group>    
          </Col>
        </Row>
        </fieldset>

        { feature_customer_mdm && origin?.pathname === "/users" &&
        <Can code="USER_MDM_LINK_VIEW">
          {/* USER_MDM_LINK_EDIT has no listed treatment, but per the
              VIEW-hide/EDIT-disable rule: visible (USER_MDM_LINK_VIEW passed)
              means the MDM field(s) inside MdmDetail should be disabled,
              not hidden, when USER_MDM_LINK_EDIT is absent. That disabling
              needs to happen inside mdmDetails.jsx itself*/}
          <MdmDetail
            handleSubmit={handleSubmit(handleSaveUser)}
            setValue={setValue}
            register={register} 
            hasphone={hasPhone}
            watch={watch}
            phonefield={watch('phone')}
            errors={errors}
            />
        </Can>
        }

        <Row>
          <Col>
            <div className="d-grid gap-2">
              <Can any={['USER_EDIT', 'USER_MDM_LINK_EDIT']}>
                <Button
                  data-testid="submit_input"
                  type="submit"
                  disabled={userFieldsDisabled}>
                    Save Changes
                </Button>
              </Can>
              
              {/* remove this errormessage component */}
               <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => <strong>{message}</strong>}
              />
              {usernameError && <strong>{usernameError}</strong> }
              {errors?.username && <strong>{errors?.username?.message}</strong>}
              {errors?.firstname && <strong>{errors?.firstname?.message}</strong>}
              {errors?.lastname && <strong>{errors?.lastname?.message}</strong>}
              {errors?.phone && <strong>{errors.phone.message}</strong> }
              {errors?.mdm_device_id && <strong>{errors.mdm_device_id.message}</strong>}
            </div>
          </Col>
        </Row>
      </Form>
      }
      
      { rowData?.user?.username &&
      <Row className="mb-3">
        { (origin?.pathname === "/users" && rowData?.user?.username) ? 
          <Col>
          <Can code="USER_DELETE">
            <DeleteUserConfirmModal onChange={handleDeleteUser} scope={"user"} 
            message={`Deleting this user will unpair an existing assignment.`} assignmentCount={rowData?.serial_number ? 1 : 0} />
          </Can>
        </Col>
          : null
        }

        {/* RBAC: SAFECASE_USER_ASSIGNMENT now gates only this Unassign
            action, not the view of who's assigned (which stays visible
            under SAFECASE_VIEW). */}
        { (origin?.pathname !== "/users" && rowData?.user?.username) ? 
          <Can code="SAFECASE_USER_ASSIGNMENT">
            <Col>
              <span className="link-span" onClick={handleUnassignUser}>Unassign User</span>
            </Col>
          </Can>
          : null
        }
      </Row>
      }

      { rowData?.user?.username === undefined &&
        <Row>
          <Col>
          <div className="d-grid gap-2">
            {/* RBAC: SAFECASE_USER_ASSIGNMENT gates the Assign New User
                trigger - since this button is the only way to reveal the
                secondary assignment form below, gating it here is sufficient. */}
            { origin?.pathname !== "/users" ? 
              <Can code="SAFECASE_USER_ASSIGNMENT">
                <Button onClick={() => { setShowUserAssign(true);}}
                variant= { showUserAssign ? "secondary" : "primary"}
                >Assign New User
                </Button>
              </Can>
               : null}
          </div>
          </Col>
            
        </Row>
      }

        { /*secondary form for reassigning SC */ origin?.pathname !== "/users" && showUserAssign  &&
          <Form className="mb-3" onSubmit={assignmentHandleSubmit(handleAssignUser)}>
            <Row>
              <Col>
                <div className="d-grid gap-2">
                  <Form.Group className="my-3">
                    <Form.Label >User ID To Assign</Form.Label>
                    <Form.Control  
                      {...assignmentRegister("newusername")}
                      type="text"
                      placeholder="jsmith"
                      className={cx({'error-input': newUsernameError})}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col>
                <div className="d-grid gap-2">
                  <Button type="submit">Confirm New Assignment</Button>
                  { newUsernameError && <strong>{newUsernameError}</strong> }
                </div>
              </Col>
            </Row>
          </Form>
        }
      
    </div>
  );
}

const GET_USERID = gql`
  query getUserId($filters: GenericFilters!) {
    Users (filters: $filters) {
      result {
        id
        username
      }
    }
  }
`;

const GET_DEVICE_BY_USERID = gql`
  query getDeviceByUserID($filters: GenericFilters!) {
    Devices (filters: $filters) {
      result {
        id
      }
    }
  }
`;

export default compose(
  withAlerts
)(UserDetails);
